import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api from "../lib/api";
import Layout from "../components/Layout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Download, Save, Sparkles, AlertTriangle, GripVertical, ArrowUp, ArrowDown, X as XIcon, Plus, History } from "lucide-react";
import { toast, Toaster } from "sonner";
import { API } from "../lib/api";
import ShareReportDialog from "../components/ShareReportDialog";
import ReportContent from "../components/ReportContent";
import KeyInsights from "../components/KeyInsights";
import Highlights from "../components/Highlights";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";

function BlockHistoryButton({ pid, blockKey, blockTitle }) {
  const [open, setOpen] = useState(false);
  const [entries, setEntries] = useState(null);
  const [loading, setLoading] = useState(false);

  const loadHistory = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/programs/${pid}/report/audit-log`, { params: { block_key: blockKey } });
      setEntries(r.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load history");
    } finally {
      setLoading(false);
    }
  };

  const handleOpen = (v) => {
    setOpen(v);
    if (v && entries === null) loadHistory();
  };

  return (
    <>
      <button
        type="button"
        onClick={() => handleOpen(true)}
        className="text-[11px] text-slate-500 hover:text-slate-900 flex items-center gap-1 transition"
        data-testid={`block-history-btn-${blockKey}`}
      >
        <History className="w-3 h-3" /> History
      </button>
      <Dialog open={open} onOpenChange={handleOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col" data-testid={`block-history-dialog-${blockKey}`}>
          <DialogHeader>
            <DialogTitle>Edit history — {blockTitle}</DialogTitle>
            <DialogDescription>
              Who changed this block, when, and what the content was before and after.
            </DialogDescription>
          </DialogHeader>
          <div className="overflow-y-auto pr-1 space-y-4" data-testid={`block-history-list-${blockKey}`}>
            {loading && <p className="text-sm text-slate-500">Loading…</p>}
            {!loading && entries && entries.length === 0 && (
              <p className="text-sm text-slate-500" data-testid={`block-history-empty-${blockKey}`}>
                No recorded edits yet. As soon as someone saves a change to this block, it will appear here.
              </p>
            )}
            {!loading && entries && entries.map((e, i) => (
              <div key={e.id || i} className="border border-slate-200 rounded-sm p-3 bg-slate-50">
                <div className="flex items-baseline justify-between text-xs text-slate-600">
                  <span className="font-medium text-slate-800">{e.by_user_name || e.by_user_email || "Unknown"}</span>
                  <span className="font-mono">{new Date(e.ts).toLocaleString()}</span>
                </div>
                {e.prev_content ? (
                  <div className="mt-2 text-xs">
                    <p className="label-overline mb-1">Before</p>
                    <p className="text-slate-500 bg-white border border-slate-200 p-2 whitespace-pre-wrap line-through">{e.prev_content}</p>
                  </div>
                ) : (
                  <p className="mt-2 text-xs text-slate-400 italic">— previously empty —</p>
                )}
                <div className="mt-2 text-xs">
                  <p className="label-overline mb-1">After</p>
                  <p className="text-slate-800 bg-white border border-emerald-200 p-2 whitespace-pre-wrap">{e.new_content}</p>
                </div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

function HighlightsPicker({ indicators, order, onToggle, onMove, onReorder }) {
  const dragRef = useRef(null);
  const indById = Object.fromEntries(indicators.map((i) => [i.id, i]));
  const featured = order.map((id) => indById[id]).filter(Boolean);
  const available = indicators.filter((i) => !order.includes(i.id));

  const onDragStart = (id) => (e) => {
    dragRef.current = id;
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", id);
  };
  const onDragOver = (e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; };
  const onDrop = (toId) => (e) => {
    e.preventDefault();
    const fromId = dragRef.current || e.dataTransfer.getData("text/plain");
    if (fromId) onReorder(fromId, toId);
    dragRef.current = null;
  };

  return (
    <div className="mt-4 space-y-3" data-testid="highlights-picker">
      <div>
        <p className="text-[11px] tracking-wider uppercase text-slate-500 mb-2">Featured order — drag to reorder donors' first impression</p>
        {featured.length === 0 ? (
          <div className="border border-dashed border-slate-300 rounded-sm p-4 text-center text-xs text-slate-500" data-testid="highlights-empty">
            No highlights selected. Pick from the indicators below to feature up to 3 hero stats.
          </div>
        ) : (
          <ul className="space-y-2" data-testid="highlights-featured-list">
            {featured.map((ind, i) => (
              <li
                key={ind.id}
                draggable
                onDragStart={onDragStart(ind.id)}
                onDragOver={onDragOver}
                onDrop={onDrop(ind.id)}
                className="group flex items-center gap-2 border border-slate-900 bg-slate-50 rounded-sm pl-2 pr-1 py-2 cursor-grab active:cursor-grabbing"
                data-testid={`highlight-featured-${ind.code}`}
              >
                <GripVertical className="w-4 h-4 text-slate-400 group-hover:text-slate-700 shrink-0"/>
                <span className="font-mono text-[10px] text-slate-500 w-6 shrink-0">#{i + 1}</span>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{ind.name}</div>
                  <div className="text-[10px] tracking-wider uppercase text-slate-500">{ind.code} · {ind.data_type}</div>
                </div>
                <button type="button" onClick={() => onMove(ind.id, -1)} disabled={i === 0} className="p-1 text-slate-500 hover:text-slate-900 disabled:opacity-30 disabled:cursor-not-allowed" data-testid={`highlight-up-${ind.code}`}><ArrowUp className="w-4 h-4"/></button>
                <button type="button" onClick={() => onMove(ind.id, 1)} disabled={i === featured.length - 1} className="p-1 text-slate-500 hover:text-slate-900 disabled:opacity-30 disabled:cursor-not-allowed" data-testid={`highlight-down-${ind.code}`}><ArrowDown className="w-4 h-4"/></button>
                <button type="button" onClick={() => onToggle(ind.id)} className="p-1 text-slate-400 hover:text-red-600" data-testid={`highlight-remove-${ind.code}`}><XIcon className="w-4 h-4"/></button>
              </li>
            ))}
          </ul>
        )}
      </div>
      {available.length > 0 && (
        <div>
          <p className="text-[11px] tracking-wider uppercase text-slate-500 mb-2">Available indicators</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2" data-testid="highlights-available-list">
            {available.map((ind) => {
              const isFull = order.length >= 3;
              return (
                <button
                  key={ind.id}
                  type="button"
                  onClick={() => onToggle(ind.id)}
                  disabled={isFull}
                  className={`flex items-center gap-3 border rounded-sm px-3 py-2 text-left transition-colors ${isFull ? "border-slate-100 bg-slate-50 text-slate-400 cursor-not-allowed" : "border-slate-200 hover:border-slate-900 hover:bg-slate-50"}`}
                  data-testid={`highlight-add-${ind.code}`}
                >
                  <Plus className="w-4 h-4 shrink-0"/>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{ind.name}</div>
                    <div className="text-[10px] tracking-wider uppercase text-slate-500">{ind.code} · {ind.data_type}</div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

export default function Report() {
  const { pid } = useParams();
  const [data, setData] = useState(null);
  const [report, setReport] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const r = await api.get(`/programs/${pid}/report`);
    setData(r.data);
    setReport(r.data.report);
  };
  useEffect(() => { load(); }, [pid]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/programs/${pid}/report`, {
        title: report.title, period: report.period, blocks: report.blocks,
        highlight_indicator_ids: report.highlight_indicator_ids || [],
        highlight_captions: report.highlight_captions || {},
      });
      toast.success("Report saved");
      load();
    } finally { setSaving(false); }
  };

  const setCaption = (id, text) => {
    setReport({
      ...report,
      highlight_captions: { ...(report.highlight_captions || {}), [id]: text },
    });
  };

  const [suggesting, setSuggesting] = useState({});
  const [usage, setUsage] = useState(null);
  const [generatingToC, setGeneratingToC] = useState(false);
  const [generatingBlock, setGeneratingBlock] = useState({});

  const generateBlock = async (blockKey) => {
    setGeneratingBlock((s) => ({ ...s, [blockKey]: true }));
    try {
      const { data: resp } = await api.post(`/programs/${pid}/report/blocks/${blockKey}/generate`);
      const text = (resp?.text || "").trim();
      if (!text) {
        toast.error("AI returned empty draft");
        return;
      }
      const blocks = [...(report.blocks || [])];
      const idx = blocks.findIndex((b) => b.key === blockKey);
      if (idx >= 0) {
        blocks[idx] = { ...blocks[idx], content: text };
      } else {
        const titles = {
          executive_summary: "Executive summary",
          achievements: "Key achievements",
          challenges: "Challenges & lessons learned",
        };
        blocks.push({ key: blockKey, title: titles[blockKey] || blockKey, content: text });
      }
      setReport({ ...report, blocks });
      toast.success("Draft generated — review + Save to publish");
      if (resp?.usage) setUsage(resp.usage);
    } catch (err) {
      toast.error(err.response?.data?.detail || "AI service unavailable");
    } finally {
      setGeneratingBlock((s) => ({ ...s, [blockKey]: false }));
    }
  };

  const generateNarrative = async () => {
    setGeneratingToC(true);
    try {
      const { data } = await api.post(`/programs/${pid}/theory-of-change/generate`);
      const narrative = (data?.narrative || "").trim();
      if (!narrative) {
        toast.error("AI returned empty narrative");
        return;
      }
      const blocks = [...(report.blocks || [])];
      const idx = blocks.findIndex((b) => b.key === "theory_of_change");
      const tocBlock = { key: "theory_of_change", title: "Theory of Change", content: narrative };
      if (idx >= 0) {
        blocks[idx] = tocBlock;
      } else {
        blocks.unshift(tocBlock);
      }
      setReport({ ...report, blocks });
      toast.success("Narrative generated — review + save to publish");
      if (data?.usage) setUsage(data.usage);
    } catch (err) {
      toast.error(err.response?.data?.detail || "AI service unavailable");
    } finally {
      setGeneratingToC(false);
    }
  };

  useEffect(() => {
    api.get("/llm-usage/captions").then((r) => setUsage(r.data)).catch(() => {});
  }, [pid]);

  const suggestCaption = async (indicatorId) => {
    setSuggesting((s) => ({ ...s, [indicatorId]: true }));
    try {
      const { data } = await api.post(`/programs/${pid}/highlights/${indicatorId}/suggest-caption`);
      if (data?.caption) {
        setCaption(indicatorId, data.caption);
        toast.success(data.cached ? "Cached draft (no AI credits used)" : "Draft generated — edit to taste");
      }
      if (data?.usage) setUsage(data.usage);
    } catch (err) {
      toast.error(err.response?.data?.detail || "AI service unavailable");
      api.get("/llm-usage/captions").then((r) => setUsage(r.data)).catch(() => {});
    } finally {
      setSuggesting((s) => ({ ...s, [indicatorId]: false }));
    }
  };

  const toggleHighlight = (id) => {
    const cur = report.highlight_indicator_ids || [];
    let next;
    if (cur.includes(id)) {
      next = cur.filter((x) => x !== id);
    } else if (cur.length >= 3) {
      toast.error("Pick up to 3 highlights");
      return;
    } else {
      next = [...cur, id];
    }
    setReport({ ...report, highlight_indicator_ids: next });
  };

  const moveHighlight = (id, dir) => {
    const cur = [...(report.highlight_indicator_ids || [])];
    const idx = cur.indexOf(id);
    const target = idx + dir;
    if (idx < 0 || target < 0 || target >= cur.length) return;
    [cur[idx], cur[target]] = [cur[target], cur[idx]];
    setReport({ ...report, highlight_indicator_ids: cur });
  };

  const reorderHighlights = (fromId, toId) => {
    if (fromId === toId) return;
    const cur = [...(report.highlight_indicator_ids || [])];
    const fromIdx = cur.indexOf(fromId);
    const toIdx = cur.indexOf(toId);
    if (fromIdx < 0 || toIdx < 0) return;
    cur.splice(fromIdx, 1);
    cur.splice(toIdx, 0, fromId);
    setReport({ ...report, highlight_indicator_ids: cur });
  };

  const updateBlock = (idx, content) => {
    const blocks = [...report.blocks];
    blocks[idx] = { ...blocks[idx], content };
    setReport({ ...report, blocks });
  };

  const downloadPdf = () => {
    window.open(`${API}/programs/${pid}/report/pdf`, "_blank");
  };

  const auditFetcher = async (indicator_id, period) => {
    const r = await api.get(`/programs/${pid}/audit`, { params: { indicator_id, period } });
    return r.data;
  };

  if (!data || !report) return <Layout><div className="text-slate-500">Loading report…</div></Layout>;

  return (
    <Layout>
      <Toaster richColors />
      <div className="flex items-start justify-between gap-6 flex-wrap">
        <div>
          <Link to={`/programs/${pid}`} className="label-overline hover:text-slate-900" data-testid="back-program-link">← Back to program</Link>
          <h1 className="font-heading text-4xl tracking-tight font-semibold mt-3" data-testid="report-program-name">{data.program.name}</h1>
          <p className="text-slate-600 mt-1">Live report. Quantitative figures are <strong>locked</strong> to source data. Qualitative blocks are editable.</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <ShareReportDialog pid={pid} />
          <Button variant="outline" onClick={save} disabled={saving} data-testid="save-report-button"><Save className="w-4 h-4 mr-1"/> {saving ? "Saving…" : "Save"}</Button>
          <Button onClick={downloadPdf} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="download-pdf-button"><Download className="w-4 h-4 mr-1"/> Download PDF</Button>
        </div>
      </div>

      <div className="mt-8 bg-white border border-slate-200 rounded-sm">
        <div className="p-6 border-b border-slate-100 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="label-overline">Report title</label>
            <Input value={report.title} onChange={(e)=>setReport({...report, title: e.target.value})} className="mt-2 font-heading text-xl" data-testid="report-title-input" />
          </div>
          <div>
            <label className="label-overline">Period</label>
            <Input value={report.period} onChange={(e)=>setReport({...report, period: e.target.value})} className="mt-2" placeholder="e.g. Jan – Jun 2025" data-testid="report-period-input"/>
          </div>
        </div>

        <div className="p-6 border-b border-slate-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="label-overline">Featured highlights</p>
              <p className="text-xs text-slate-500 mt-1">Pick up to 3 indicators to feature as hero stats on your public donor page.</p>
            </div>
            <span className="text-xs text-slate-500">{(report.highlight_indicator_ids || []).length}/3 selected</span>
          </div>
          <HighlightsPicker
            indicators={data.indicators}
            order={report.highlight_indicator_ids || []}
            onToggle={toggleHighlight}
            onMove={moveHighlight}
            onReorder={reorderHighlights}
          />
          {data.highlights?.length > 0 && (
            <div className="mt-6 space-y-4">
              <div>
                <p className="label-overline mb-3 flex items-center justify-between">
                  <span>Tell donors why <span className="text-slate-400 normal-case tracking-normal">— optional one-line caption per highlight</span></span>
                  {usage && (
                    <span
                      className={`normal-case tracking-normal text-[11px] font-mono px-2 py-0.5 rounded border ${
                        usage.remaining === 0 ? "border-red-300 bg-red-50 text-red-700" :
                        usage.remaining <= 3 ? "border-amber-300 bg-amber-50 text-amber-700" :
                        "border-slate-200 bg-slate-50 text-slate-600"
                      }`}
                      data-testid="ai-usage-indicator"
                    >
                      {usage.remaining} / {usage.limit} AI drafts left this minute
                    </span>
                  )}
                </p>
                <div className="space-y-2" data-testid="highlight-captions-editor">
                  {data.highlights.map((h) => (
                    <div key={h.indicator_id} className="space-y-1">
                      <div className="flex items-center gap-3">
                        <span className="text-xs font-mono text-slate-500 w-24 shrink-0 truncate">{h.value_display} {h.unit}</span>
                        <Input
                          value={(report.highlight_captions || {})[h.indicator_id] ?? h.caption ?? ""}
                          onChange={(e)=>setCaption(h.indicator_id, e.target.value)}
                          placeholder={`e.g. driven by ${h.label.toLowerCase()} expansion in rural districts`}
                          className={`flex-1 ${h.stale ? "border-amber-400 focus-visible:ring-amber-400" : ""}`}
                          data-testid={`caption-input-${h.indicator_id}`}
                        />
                        <Button
                          variant={h.stale ? "default" : "outline"}
                          size="sm"
                          onClick={()=>suggestCaption(h.indicator_id)}
                          disabled={!!suggesting[h.indicator_id]}
                          data-testid={`ai-suggest-${h.indicator_id}`}
                          title={h.stale ? "Data has changed — refresh this caption" : "Draft caption with AI"}
                          className={h.stale ? "bg-amber-500 hover:bg-amber-600 text-white" : ""}
                        >
                          <Sparkles className="w-4 h-4 mr-1"/> {suggesting[h.indicator_id] ? "Drafting…" : (h.stale ? "Refresh AI draft" : "AI draft")}
                        </Button>
                      </div>
                      {h.stale && (
                        <div className="ml-[6.5rem] flex items-center gap-1 text-xs text-amber-700" data-testid={`stale-badge-${h.indicator_id}`}>
                          <AlertTriangle className="w-3 h-3"/> New data has landed since this caption was written. Donors still see the old wording — refresh or edit it.
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <p className="label-overline mb-3">Live preview</p>
                <Highlights items={data.highlights.map(h => ({
                  ...h,
                  caption: (report.highlight_captions || {})[h.indicator_id] ?? h.caption ?? "",
                }))} />
              </div>
            </div>
          )}
        </div>

        <div className="border-t border-slate-100">
          <KeyInsights pid={pid} />
        </div>

        <div className="p-6 space-y-8 border-t border-slate-100">
          <div className="flex items-center justify-end">
            <Button
              variant="outline"
              size="sm"
              onClick={generateNarrative}
              disabled={generatingToC}
              data-testid="generate-toc-button"
            >
              <Sparkles className="w-4 h-4 mr-1"/> {generatingToC ? "Generating…" : "AI: Generate Theory of Change"}
            </Button>
          </div>
          {report.blocks.map((b, idx) => {
            const aiKey = ["executive_summary", "achievements", "challenges"].includes(b.key) ? b.key : null;
            return (
              <div key={b.key} data-testid={`report-block-${b.key}`}>
                <div className="flex items-baseline justify-between flex-wrap gap-2">
                  <p className="label-overline">{b.title}</p>
                  <div className="flex items-center gap-3">
                    {aiKey && (
                      <button
                        type="button"
                        onClick={() => generateBlock(aiKey)}
                        disabled={!!generatingBlock[aiKey]}
                        className="text-[11px] text-slate-700 hover:text-slate-900 inline-flex items-center gap-1 border border-slate-200 hover:border-slate-900 px-2 py-1 rounded-sm disabled:opacity-50"
                        data-testid={`ai-block-${b.key}`}
                        title={`Draft this block from your indicator data`}
                      >
                        <Sparkles className="w-3 h-3" /> {generatingBlock[aiKey] ? "Drafting…" : "AI: Draft"}
                      </button>
                    )}
                    <BlockHistoryButton pid={pid} blockKey={b.key} blockTitle={b.title} />
                  </div>
                </div>
                <Textarea
                  value={b.content}
                  onChange={(e)=>updateBlock(idx, e.target.value)}
                  rows={b.key === "theory_of_change" ? 6 : 4}
                  className="mt-2 font-body text-base leading-relaxed"
                  data-testid={`block-input-${b.key}`}
                />
              </div>
            );
          })}
        </div>

        <div className="border-t border-slate-100">
          <ReportContent
            data={data}
            report={{ title: "Indicator results", period: "", blocks: [] }}
            auditFetcher={auditFetcher}
            showInsights={false}
          />
        </div>
      </div>
    </Layout>
  );
}
